import { useCallback, useMemo, useRef, useState } from "react";

/** XHR 기반 업로드 진행률/취소/에러 관리 훅 */
export default function useUploadWithProgress({ endpoint, withCredentials = true, headers = {} }) {
    const [progress, setProgress] = useState(0);      // 0~100
    const [bytesSent, setBytesSent] = useState(0);
    const [totalBytes, setTotalBytes] = useState(0);
    const [status, setStatus] = useState("idle");     // idle|uploading|success|error|cancelled
    const [error, setError] = useState(null);
    const [startedAt, setStartedAt] = useState(0);
    const xhrRef = useRef(null);

    const upload = useCallback((formData) => {
        setProgress(0);
        setBytesSent(0);
        setTotalBytes(0);
        setStatus("uploading");
        setError(null);
        setStartedAt(Date.now());

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhrRef.current = xhr;

            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                    const pct = Math.round((e.loaded / e.total) * 100);
                    setProgress(pct);
                    setBytesSent(e.loaded);
                    setTotalBytes(e.total);
                }
            };

            xhr.onload = () => {
                const ok = xhr.status >= 200 && xhr.status < 300;
                if (!ok) {
                    const err = new Error(xhr.responseText || `HTTP ${xhr.status}`);
                    setStatus("error"); setError(err); reject(err);
                    return;
                }
                setStatus("success");
                try { resolve(JSON.parse(xhr.responseText || "{}")); }
                catch { resolve({ raw: xhr.responseText }); }
            };

            xhr.onerror = () => {
                const err = new Error("네트워크 오류");
                setStatus("error"); setError(err); reject(err);
            };

            xhr.onabort = () => {
                setStatus("cancelled");
                reject(new Error("업로드 취소됨"));
            };

            xhr.open("POST", endpoint, true);
            if (withCredentials) xhr.withCredentials = true;
            Object.entries(headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));
            xhr.send(formData);
        });
    }, [endpoint, withCredentials, headers]);

    const cancel = useCallback(() => {
        if (xhrRef.current && xhrRef.current.readyState !== 4) {
            xhrRef.current.abort();
        }
    }, []);

    const stats = useMemo(() => {
        if (status !== "uploading" || !bytesSent || !totalBytes || !startedAt) {
            return { mbps: 0, etaSec: 0 };
        }
        const elapsedSec = (Date.now() - startedAt) / 1000;
        const speedBps = bytesSent / Math.max(elapsedSec, 0.001);
        const mbps = speedBps / (1024 * 1024);
        const remainBytes = Math.max(totalBytes - bytesSent, 0);
        const etaSec = remainBytes / Math.max(speedBps, 1);
        return { mbps, etaSec };
    }, [status, bytesSent, totalBytes, startedAt]);

    return {
        upload, cancel,
        progress, status, error,
        bytesSent, totalBytes,
        mbps: stats.mbps, etaSec: stats.etaSec,
        uploading: status === "uploading",
        success: status === "success",
        failed: status === "error",
        cancelled: status === "cancelled",
    };
}
